import { useSearchParams } from '@remix-run/react';
import CustomPagination from '~/components/custom-pagination';
import StarRating from '~/components/star-rating';
import { Avatar, AvatarFallback } from '~/components/ui/avatar';
import { Progress } from '~/components/ui/progress';
import { formatDate, getInitials } from '~/lib/utils';
import { getAuctionHouseDetailsBySlug } from '~/services/api/auction-houses';
import { getReviewsList } from '~/services/api/reviews';
import { Star } from 'lucide-react';

export type Reviews = NonNullable<Awaited<ReturnType<typeof getReviewsList>>>;

interface ReviewsProps {
  reviews: Reviews;
  auctionHouseData: NonNullable<Awaited<ReturnType<typeof getAuctionHouseDetailsBySlug>>>;
}

export default function Reviews({ reviews, auctionHouseData }: ReviewsProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const auctionHouseReviews = auctionHouseData.auctions.flatMap((auction) => auction.reviews);

  const reviewsAverageRating = Number(
    (auctionHouseReviews.reduce((acc, curr) => acc + curr.rating, 0) / auctionHouseReviews.length).toFixed(1),
  );

  const reviewPercentages = () => {
    const groupedReviews: Record<number, typeof auctionHouseReviews> = {
      5: [],
      4: [],
      3: [],
      2: [],
      1: [],
    };

    auctionHouseReviews.forEach((review) => {
      if (review.rating >= 1 && review.rating <= 5) {
        groupedReviews[Math.round(review.rating)].push(review);
      }
    });

    return groupedReviews;
  };

  const limit = parseInt(searchParams.get('limit') ?? '12');
  const currentPage = reviews.page;
  const totalPages = Math.ceil(reviews.totalCount / limit);

  return (
    <>
      <div className="mb-5 mt-2 flex flex-col">
        <h1 className="text-2xl font-semibold">Latest Reviews</h1>
        <span className="text-sm text-slate-600">{reviews.totalCount} reviews found</span>
      </div>

      <div className="my-4 h-[1px] bg-slate-200" />

      <div className="grid md:grid-cols-3">
        <div className="col-span-1 flex flex-col">
          <h3 className="font-medium">Total Reviews</h3>

          <div className="mt-5">
            <h2 className="text-3xl font-semibold">{reviews.totalCount}</h2>
            <span className="text-sm text-slate-500">Total ratings this year</span>
          </div>
        </div>

        <div className="col-span-1 my-5 flex flex-col border-y py-5 md:my-0 md:border-y-0 md:border-l md:px-5 md:py-0">
          <h3 className="font-medium">Average Rating</h3>

          <div className="mt-5">
            <div className="flex items-center gap-x-5">
              <h2 className="text-3xl font-semibold">{reviewsAverageRating}</h2>
              <StarRating rating={reviewsAverageRating} size={16} color="#2563eb" />
            </div>
            <span className="text-sm text-slate-500">Average rating this year</span>
          </div>
        </div>

        <div className="col-span-1 flex flex-col-reverse gap-y-1 md:border-l md:px-5">
          {/* To-do: Populate review percentages from reviewPercentages function */}
          {Array(5)
            .fill({ ratingPercentage: 0 })
            .map(({ ratingPercentage }, index) => {
              return (
                <div key={index} className="flex items-center">
                  <div className="flex basis-1/6 items-center gap-x-1">
                    <Star fill="#000" size={16} color="#000" />
                    <span>{index + 1}</span>
                  </div>

                  <div className="flex basis-3/6 items-center gap-x-2">
                    <Progress value={ratingPercentage} className="h-1 rounded-sm" />
                    <span className="text-sm font-medium">{ratingPercentage + '%'}</span>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="my-10 mb-4 flex flex-col">
        {reviews.list.length > 0 ? (
          reviews.list.map((review, index) => (
            <div key={index} className="flex flex-col border-b py-6 md:flex-row">
              <div className="flex basis-2/12 gap-x-3">
                <Avatar>
                  <AvatarFallback>
                    {getInitials(review.reviewedBy.firstName + ' ' + review.reviewedBy.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm">{review.reviewedBy.firstName + ' ' + review.reviewedBy.lastName}</span>
                  <span className="text-xs text-slate-500">
                    {`${review.reviewedBy.address.city}, ${review.reviewedBy.address.country}`}
                  </span>
                </div>
              </div>

              <div className="mt-4 flex basis-9/12 flex-col pr-4 md:mt-0">
                <div className="flex items-center gap-x-3">
                  <StarRating rating={review.rating} size={14} />
                  <span className="text-xs text-gray-500">{formatDate(review.createdAt)}</span>
                </div>

                <div className="mt-4 flex flex-col">
                  <h3 className="font-semibold">{review.title}</h3>
                  <span className="text-sm">{review.description}</span>
                </div>
              </div>

              <div className="mt-4 flex basis-1/12 items-center md:mt-0 md:justify-center">
                <img
                  src={review.auction.products[0].images[0].file.src}
                  className="h-20 w-20 rounded-lg object-cover"
                />
              </div>
            </div>
          ))
        ) : (
          <div className="flex w-full items-center justify-center border-t py-8">
            <span className="text-center text-sm text-slate-500">No reviews yet</span>
          </div>
        )}
      </div>

      <CustomPagination
        limit={limit}
        totalCount={reviews.totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevPage={() => {
          searchParams.set('page', (currentPage - 1).toString());
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
        onNextPage={() => {
          searchParams.set('page', (currentPage + 1).toString());
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
        onPageChange={(page) => {
          searchParams.set('page', page);
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
        onLimitChange={(limit) => {
          searchParams.set('limit', limit);
          setSearchParams(searchParams, {
            preventScrollReset: true,
          });
        }}
      />
    </>
  );
}
